import { db } from './db.js';
import { Emitter } from './utils.js';

/* Descargas para ver sin conexión. Las películas y episodios se guardan en el dispositivo:
   - Web/PWA y escritorio: en el almacenamiento privado del navegador (OPFS), troceados en partes.
     Una parte se escribe entera o no se escribe, así que tras cerrar la app a medias se reanuda
     desde la última parte completa pidiendo el resto con Range.
   - App Android/iOS (Cordova): en la carpeta de la app, con cordova-plugin-file.
   El índice (qué hay, estado, tamaño y los datos para reproducir sin catálogo) vive en IndexedDB.
   Se descarga de una en una. Con audio Dolby/DTS se baja ya convertido (rec.converted): ese flujo
   no admite Range, así que una descarga convertida interrumpida vuelve a empezar. */

const INDEX_KEY = 'offline:index';
const PART_SIZE = 16 * 1024 * 1024;
const MIN_FREE = 300 * 1024 * 1024;
const MAX_RETRIES = 5;
const RETRY_DELAY = 4000;

/** Por encima de este tamaño, un «text/…» es un vídeo mal etiquetado (los avisos de un proveedor son de pocas líneas). */
const TEXT_AS_VIDEO_MIN = 1048576;

/** ¿Parece binario (vídeo) y no texto? Mira el primer trozo: los vídeos están llenos de bytes de control, el texto casi no. */
export function looksBinary(bytes) {
    if (!bytes || !bytes.length) return false;
    const n = Math.min(bytes.length, 512);
    let ctrl = 0;
    for (let i = 0; i < n; i++) {
        const b = bytes[i];
        if (b < 9 || (b > 13 && b < 32)) ctrl++;
    }
    return ctrl / n > 0.02;
}

/** Qué ha contestado el proveedor cuando no es un vídeo: tipo, estado y las primeras palabras, sin direcciones (llevan la contraseña). */
export function describeBody(type, status, bytes) {
    let text = '';
    try { text = new TextDecoder('utf-8').decode(bytes ? bytes.subarray(0, 600) : new Uint8Array(0)); } catch (e) { /* ilegible */ }
    text = text.replace(/<[^>]*>/g, ' ').replace(/https?:\/\/\S+/gi, '…').replace(/\s+/g, ' ').trim().slice(0, 140);
    return [String(type || '').split(';')[0].trim(), 'HTTP ' + status, text].filter(Boolean).join(' · ');
}

const MIME = { mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', mkv: 'video/x-matroska', webm: 'video/webm', avi: 'video/x-msvideo', ts: 'video/mp2t', mpg: 'video/mpeg', mpeg: 'video/mpeg' };
// Errores que no se arreglan reintentando.
const FATAL = { no_space: 1, bad_content: 1, http_401: 1, http_403: 1, http_404: 1, http_410: 1, unsupported: 1 };

export function mimeFor(ext) {
    return MIME[String(ext || '').toLowerCase()] || 'video/mp4';
}

const safeName = (key) => String(key).replace(/[^a-zA-Z0-9_-]/g, '_');

function fail(code) {
    return Object.assign(new Error(code), { code });
}

/* ---------------- Almacenamiento: navegador (OPFS) ---------------- */

export function opfsBackend() {
    const partName = (n) => 'p' + ('000000' + n).slice(-6);

    async function base() {
        const root = await navigator.storage.getDirectory();
        return root.getDirectoryHandle('offline', { create: true });
    }

    async function parts(key) {
        let dir;
        try {
            dir = await (await base()).getDirectoryHandle(safeName(key));
        } catch (e) {
            return [];
        }
        const out = [];
        const it = dir.entries();
        for (let r = await it.next(); !r.done; r = await it.next()) {
            const name = r.value[0];
            const handle = r.value[1];
            if (handle.kind !== 'file' || !/^p\d{6}$/.test(name)) continue;
            const file = await handle.getFile();
            out.push({ name, file, size: file.size });
        }
        return out.sort((a, b) => (a.name < b.name ? -1 : 1));
    }

    return {
        name: 'opfs',

        async size(key) {
            return (await parts(key)).reduce((n, p) => n + p.size, 0);
        },

        async writer(key) {
            const dir = await (await base()).getDirectoryHandle(safeName(key), { create: true });
            const existing = await parts(key);
            let index = existing.length ? Number(existing[existing.length - 1].name.slice(1)) + 1 : 0;
            let chunks = [];
            let buffered = 0;
            const flush = async () => {
                if (!buffered) return;
                const handle = await dir.getFileHandle(partName(index), { create: true });
                const w = await handle.createWritable();
                await w.write(new Blob(chunks));
                await w.close();
                index++;
                chunks = [];
                buffered = 0;
            };
            return {
                async write(chunk) {
                    chunks.push(chunk);
                    buffered += chunk.byteLength;
                    if (buffered >= PART_SIZE) await flush();
                },
                close: flush
            };
        },

        /** El vídeo como un único Blob hecho de las partes (no las copia a memoria). */
        async open(key, type) {
            const list = await parts(key);
            if (!list.length) return null;
            const url = URL.createObjectURL(new Blob(list.map((p) => p.file), { type }));
            return { url, release: () => URL.revokeObjectURL(url) };
        },

        async remove(key) {
            try {
                await (await base()).removeEntry(safeName(key), { recursive: true });
            } catch (e) { /* no existía */ }
        },

        async usage() {
            try {
                const est = await navigator.storage.estimate();
                return { free: Math.max(0, (est.quota || 0) - (est.usage || 0)) };
            } catch (e) {
                return { free: null };
            }
        },

        /** Pide al navegador que no borre estos datos si el dispositivo anda justo de espacio. */
        async persist() {
            try {
                if (navigator.storage.persist) await navigator.storage.persist();
            } catch (e) { /* sin permiso: se sigue igual */ }
        }
    };
}

export function opfsAvailable() {
    try {
        return !!(window.isSecureContext && navigator.storage && navigator.storage.getDirectory
            && window.FileSystemFileHandle && 'createWritable' in window.FileSystemFileHandle.prototype
            && window.ReadableStream);
    } catch (e) {
        return false;
    }
}

/* ---------------- Almacenamiento: app nativa (cordova-plugin-file) ---------------- */

export function cordovaBackend() {
    const WRITE_SIZE = 4 * 1024 * 1024;
    const root = () => window.cordova.file.externalDataDirectory || window.cordova.file.dataDirectory;
    const p = (fn) => new Promise((resolve, reject) => fn(resolve, reject));

    async function dir() {
        const base = await p((ok, ko) => window.resolveLocalFileSystemURL(root(), ok, ko));
        return p((ok, ko) => base.getDirectory('offline', { create: true }, ok, ko));
    }

    async function entry(key, ext, create) {
        const d = await dir();
        return p((ok, ko) => d.getFile(safeName(key) + '.' + (ext || 'mp4'), { create: !!create }, ok, ko));
    }

    // El nombre lleva la extensión real (el reproductor del sistema la usa); al borrar o medir
    // solo se conoce la clave, así que se busca el fichero que empieza por ella.
    async function find(key) {
        const d = await dir();
        const list = await p((ok, ko) => d.createReader().readEntries(ok, ko));
        const prefix = safeName(key) + '.';
        return list.find((e) => e.isFile && e.name.indexOf(prefix) === 0) || null;
    }

    return {
        name: 'cordova',

        async size(key) {
            const e = await find(key).catch(() => null);
            if (!e) return 0;
            const f = await p((ok, ko) => e.file(ok, ko));
            return f.size;
        },

        async writer(key, ext) {
            const e = (await find(key).catch(() => null)) || (await entry(key, ext, true));
            const w = await p((ok, ko) => e.createWriter(ok, ko));
            w.seek(w.length);
            let chunks = [];
            let buffered = 0;
            const flush = async () => {
                if (!buffered) return;
                const blob = new Blob(chunks);
                chunks = [];
                buffered = 0;
                await new Promise((resolve, reject) => {
                    w.onwriteend = () => resolve();
                    w.onerror = (err) => reject(err && err.target && err.target.error ? err.target.error : new Error('write'));
                    w.write(blob);
                });
            };
            return {
                async write(chunk) {
                    chunks.push(chunk);
                    buffered += chunk.byteLength;
                    if (buffered >= WRITE_SIZE) await flush();
                },
                close: flush
            };
        },

        async open(key) {
            const e = await find(key).catch(() => null);
            return e ? { url: e.toURL() || e.nativeURL, release() {} } : null;
        },

        async remove(key) {
            const e = await find(key).catch(() => null);
            if (e) await p((ok, ko) => e.remove(ok, ko)).catch(() => {});
        },

        async usage() {
            try {
                const kb = await p((ok, ko) => window.cordova.exec(ok, ko, 'File', 'getFreeDiskSpace', []));
                return { free: Number(kb) * 1024 };
            } catch (e) {
                return { free: null };
            }
        },

        async persist() {}
    };
}

export function cordovaAvailable() {
    return !!(window.cordova && window.cordova.file && window.resolveLocalFileSystemURL && window.ReadableStream);
}

/* ---------------- Gestor ---------------- */

export class Offline extends Emitter {
    constructor({ backend = null, fetch: fetchFn = null, sourceFor = null, verify = null, isOnline = null, retryDelay = RETRY_DELAY } = {}) {
        super();
        this.backend = backend;
        this.fetch = fetchFn;
        this.sourceFor = sourceFor;
        // verify(rec) → false si lo bajado está cortado. Solo se usa cuando el servidor no dice el
        // tamaño (descargas convertidas): ahí un corte parecería un final normal.
        this.verify = verify;
        this.isOnline = isOnline || (() => typeof navigator === 'undefined' || navigator.onLine !== false);
        this.retryDelay = retryDelay;
        this.items = new Map();
        this.ready = false;
        this._active = null;
        this._running = false;
        this._lastTick = 0;
        this._lastSave = 0;
        this.disabled = false; // la demo pública no descarga nada (lo pone main.js): ni botones, ni página, ni cola
    }

    get supported() {
        return !!this.backend && !this.disabled;
    }

    static key(profileId, kind, id) {
        return (profileId || 'local') + ':' + kind + ':' + id;
    }

    async init() {
        if (this.ready) return this;
        const saved = this.supported ? await db.get('meta', INDEX_KEY) : null;
        (saved || []).forEach((rec) => {
            // Lo que estaba bajando cuando se cerró la app sigue en la cola.
            if (rec.status === 'downloading') rec.status = 'queued';
            this.items.set(rec.key, rec);
        });
        this.ready = true;
        if (typeof window !== 'undefined' && window.addEventListener) {
            window.addEventListener('online', () => this.resumeWaiting());
        }
        this._run();
        return this;
    }

    list() {
        return Array.from(this.items.values()).sort((a, b) => b.createdAt - a.createdAt);
    }

    get(key) {
        return this.items.get(key) || null;
    }

    hasDone() {
        return this.list().some((r) => r.status === 'done');
    }

    totalBytes() {
        return this.list().reduce((n, r) => n + (r.received || 0), 0);
    }

    async usage() {
        const u = this.supported ? await this.backend.usage() : { free: null };
        return { used: this.totalBytes(), free: u.free };
    }

    /** Añade una descarga a la cola. `rec` trae key, título, URL del proveedor y datos para reproducir. */
    async add(rec) {
        if (!this.supported) throw fail('unsupported');
        const prev = this.items.get(rec.key);
        if (prev) {
            if (prev.status === 'paused' || prev.status === 'error') return this.resume(rec.key);
            return prev;
        }
        const item = Object.assign({ size: 0, received: 0, retries: 0, error: '', createdAt: Date.now(), finishedAt: 0 }, rec, { status: 'queued', waiting: false });
        this.items.set(item.key, item);
        if (this.backend.persist) this.backend.persist();
        await this._save();
        this.emit('change', item);
        this._run();
        return item;
    }

    async pause(key) {
        const rec = this.items.get(key);
        if (!rec || rec.status === 'done') return;
        rec.waiting = false;
        if (this._active && this._active.key === key) {
            this._active.reason = 'pause';
            this._active.ctrl.abort();
            return;
        }
        rec.status = 'paused';
        await this._save();
        this.emit('change', rec);
    }

    async resume(key) {
        const rec = this.items.get(key);
        if (!rec || rec.status === 'done' || rec.status === 'downloading') return rec;
        rec.status = 'queued';
        rec.error = '';
        rec.retries = 0;
        rec.waiting = false;
        await this._save();
        this.emit('change', rec);
        this._run();
        return rec;
    }

    /** Vuelve la conexión: siguen las descargas que se quedaron esperándola. */
    resumeWaiting() {
        let any = false;
        this.items.forEach((rec) => {
            if (rec.waiting && rec.status === 'paused') {
                rec.status = 'queued';
                rec.waiting = false;
                any = true;
                this.emit('change', rec);
            }
        });
        if (any) this._save();
        this._run();
    }

    async remove(key) {
        const rec = this.items.get(key);
        if (!rec) return;
        this.items.delete(key);
        if (this._active && this._active.key === key) {
            this._active.reason = 'remove';
            this._active.ctrl.abort();
            await this._active.done;
        }
        await this.backend.remove(key);
        await this._save();
        this.emit('change', rec);
    }

    /** URL reproducible de una descarga terminada (o null). Llamar a release() al cerrar.
        `unfinished` deja abrir una a medias: solo para comprobarla antes de darla por buena. */
    async open(key, { unfinished = false } = {}) {
        const rec = this.items.get(key);
        if (!rec || !this.supported || (rec.status !== 'done' && !unfinished)) return null;
        try {
            return await this.backend.open(key, mimeFor(rec.ext));
        } catch (e) {
            return null;
        }
    }

    async _save() {
        this._lastSave = Date.now();
        await db.put('meta', INDEX_KEY, this.list());
    }

    _tick(rec) {
        const now = Date.now();
        if (now - this._lastTick > 400) {
            this._lastTick = now;
            this.emit('progress', rec);
        }
        if (now - this._lastSave > 5000) this._save();
    }

    async _run() {
        if (!this.supported || !this.ready) return;
        if (this._running) {
            // Llegó algo a la cola mientras el bucle terminaba: que dé otra vuelta al acabar.
            this._again = true;
            return;
        }
        this._running = true;
        try {
            for (;;) {
                const next = this.list().reverse().find((r) => r.status === 'queued');
                if (!next) break;
                if (!this.isOnline()) {
                    this.items.forEach((r) => {
                        if (r.status === 'queued') { r.status = 'paused'; r.waiting = true; this.emit('change', r); }
                    });
                    await this._save();
                    break;
                }
                const active = { key: next.key, ctrl: new AbortController(), reason: '' };
                active.done = this._download(next, active);
                this._active = active;
                await active.done;
                this._active = null;
            }
        } finally {
            this._running = false;
            if (this._again) {
                this._again = false;
                this._run();
            }
        }
    }

    async _download(rec, active) {
        const backend = this.backend;
        let writer = null;
        rec.status = 'downloading';
        rec.error = '';
        this.emit('change', rec);
        try {
            let offset = await backend.size(rec.key);
            if (rec.converted && offset) {
                await backend.remove(rec.key);
                offset = 0;
            }
            rec.received = offset;
            if (!(rec.size && offset >= rec.size)) {
                const headers = {};
                if (offset > 0) headers.Range = 'bytes=' + offset + '-';
                const res = await this.fetch(await this.sourceFor(rec), { headers, cache: 'no-store', signal: active.ctrl.signal });
                const complete = res.status === 416 && offset > 0;
                if (!complete) {
                    if (!res.ok) throw fail('http_' + res.status);
                    let peeked = null;
                    const type = res.headers.get('content-type') || '';
                    if (/^(text\/|application\/json)/i.test(type)) {
                        // Algunos proveedores etiquetan el vídeo como texto: si lo que viene es grande o es binario, es un vídeo. Si no, es un
                        // aviso del proveedor (conexiones agotadas, cuenta caducada…): se guarda qué dice para enseñarlo.
                        const len = Math.max(Number(res.headers.get('content-length')) || 0, Number((/\/(\d+)\s*$/.exec(res.headers.get('content-range') || '') || [])[1]) || 0);
                        if (len <= TEXT_AS_VIDEO_MIN) {
                            const probe = res.body.getReader();
                            const first = await probe.read();
                            if (first.value && looksBinary(first.value)) {
                                peeked = { reader: probe, chunk: first.value };
                            } else {
                                rec.detail = describeBody(type, res.status, first.value);
                                try { await probe.cancel(); } catch (e) { /* ya cerrado */ }
                                throw fail('bad_content');
                            }
                        }
                    }
                    if (offset > 0 && res.status !== 206) {
                        // El servidor no ha hecho caso al Range: se empieza de nuevo.
                        await backend.remove(rec.key);
                        offset = 0;
                        rec.received = 0;
                    }
                    const range = /\/(\d+)\s*$/.exec(res.headers.get('content-range') || '');
                    const length = Number(res.headers.get('content-length')) || 0;
                    const total = range ? Number(range[1]) : (length ? offset + length : 0);
                    rec.size = total;
                    if (total) {
                        const u = await backend.usage();
                        if (u.free != null && total - offset > u.free - MIN_FREE) throw fail('no_space');
                    }
                    writer = await backend.writer(rec.key, rec.ext);
                    const reader = peeked ? peeked.reader : res.body.getReader();
                    if (peeked) {
                        await writer.write(peeked.chunk);
                        rec.received += peeked.chunk.byteLength;
                    }
                    for (;;) {
                        const step = await reader.read();
                        if (step.done) break;
                        await writer.write(step.value);
                        rec.received += step.value.byteLength;
                        this._tick(rec);
                    }
                    await writer.close();
                    writer = null;
                    if (!rec.received) throw fail('empty');
                    if (rec.size && rec.received < rec.size) throw fail('incomplete');
                    if (!rec.size && this.verify) {
                        const ok = await this.verify(rec).catch(() => true);
                        // Dos intentos seguidos con el mismo tamaño: el vídeo es así de largo y es
                        // la duración de la ficha la que no coincide. Se da por bueno.
                        const same = rec.lastSize && Math.abs(rec.lastSize - rec.received) <= rec.received * 0.01;
                        rec.lastSize = rec.received;
                        if (!ok && !same) throw fail('incomplete');
                    }
                }
            }
            rec.size = rec.received;
            rec.status = 'done';
            rec.finishedAt = Date.now();
            rec.retries = 0;
            rec.badTries = 0;
            rec.detail = '';
        } catch (err) {
            if (writer) {
                try { await writer.close(); } catch (e) { /* se pierde el último tramo */ }
            }
            if (active.reason === 'remove') return;
            rec.received = await backend.size(rec.key).catch(() => rec.received);
            const code = err && err.code ? err.code : (err && /quota/i.test(String(err.name || err.message)) ? 'no_space' : 'network');
            if (active.reason === 'pause') {
                rec.status = 'paused';
            } else if (FATAL[code] && !(code === 'bad_content' && (rec.badTries = (rec.badTries || 0) + 1) <= 2)) {
                // «bad_content» se reintenta dos veces: casi siempre es el proveedor con sus conexiones ocupadas (p. ej. la vista previa que
                // acaba de cerrarse) y a los pocos segundos contesta bien.
                rec.status = 'error';
                rec.error = code;
            } else if (!this.isOnline()) {
                rec.status = 'paused';
                rec.waiting = true;
            } else if ((rec.retries = (rec.retries || 0) + 1) > MAX_RETRIES) {
                rec.status = 'error';
                rec.error = code;
            } else {
                rec.status = 'queued';
                await new Promise((resolve) => setTimeout(resolve, this.retryDelay));
                if (!this.items.has(rec.key)) return;
                if (active.reason === 'pause') rec.status = 'paused';
            }
        }
        await this._save();
        this.emit('change', rec);
    }
}

function pickBackend() {
    if (typeof window === 'undefined') return null;
    if (cordovaAvailable()) return cordovaBackend();
    if (opfsAvailable()) return opfsBackend();
    return null;
}

export const offline = new Offline();

/** Se llama al arrancar (tras deviceready en la app nativa): elige almacenamiento y carga el índice. */
export function setupOffline({ sourceFor }) {
    offline.backend = pickBackend();
    offline.fetch = (url, opts) => fetch(url, opts);
    offline.sourceFor = sourceFor;
    offline.verify = verifyDuration;
    return offline.init();
}

/**
 * Comprueba que un vídeo bajado sin tamaño conocido dura lo que debe: lo abre en un <video>
 * oculto y compara con la duración de la ficha. Si no se puede saber (sin dato en la ficha o
 * códec que este dispositivo no abre) se da por bueno.
 */
async function verifyDuration(rec) {
    const expected = Number(rec.durationSecs) || 0;
    if (!expected || typeof document === 'undefined') return true;
    const local = await offline.open(rec.key, { unfinished: true });
    if (!local) return false;
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    try {
        const actual = await new Promise((resolve) => {
            const timer = setTimeout(() => resolve(0), 15000);
            video.onloadedmetadata = () => { clearTimeout(timer); resolve(isFinite(video.duration) ? video.duration : 0); };
            video.onerror = () => { clearTimeout(timer); resolve(0); };
            video.src = local.url;
        });
        return !actual || actual >= expected * 0.9;
    } finally {
        video.removeAttribute('src');
        try { video.load(); } catch (e) { /* nada */ }
        local.release();
    }
}
